import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import PersonIcon from '@mui/icons-material/Person';
import SportsIcon from '@mui/icons-material/Sports';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logOut } from '../../../../adapters/ServiceAPI';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useLoadLeagueDataQuickView, useLoadTradeData } from '../../../../app/dataLoaderHooks';
import LoginDialog from '../../../common/LoginDialog';


interface MenuOption {
  label: string;
  icon: React.ReactNode;
  action: string;
  divider?: boolean;
  badge?: number;
}

function ProfileButton() {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElTrades, setAnchorElTrades] = React.useState<null | HTMLElement>(null);
  const [anchorElLeagues, setAnchorElLeagues] = React.useState<null | HTMLElement>(null);
  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();

  const user = useAppSelector((state) => state.user);
  const isInLeague = Boolean(leagueId);
  
  // Load user's leagues
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  
  const userLeagues = useLoadLeagueDataQuickView({
    userId: user?.id,
    loadRoster: false,
    setLoadComplete,
    setIsException
  }) || [];
  
  // Get current user's team in the active league
  const userTeam = React.useMemo(() => {
    if (!isInLeague || !leagueId) return undefined;
    const currentLeague = userLeagues.find(league => league.id === leagueId);
    return currentLeague?.teams?.find(team => 
      team?.owners?.some(owner => owner.id === user?.id)
    );
  }, [isInLeague, leagueId, userLeagues, user?.id]);

  // Load trade data - always call hook but use safe teamId
  const [isTradeLoadComplete, setTradeLoadComplete] = React.useState(false);
  const trades = useLoadTradeData({
    teamId: userTeam?.id || '', // Safe default
    setIsException: setIsException,
    isLoadComplete: isTradeLoadComplete,
    setLoadComplete: setTradeLoadComplete
  });
  
  const pendingTrades = Array.isArray(trades) && userTeam?.id ? 
    trades.filter(trade => trade.status === 'PENDING') : [];
  const hasMultipleLeagues = userLeagues.length > 1;
  
  // Get current league data for team names
  const currentLeague = React.useMemo(() => {
    return userLeagues.find(league => league.id === leagueId);
  }, [userLeagues, leagueId]);
  
  // Helper function to get trade partner name
  const getTradePartnerName = React.useCallback((trade: any) => {
    if (!trade.teams || !userTeam?.id || !currentLeague?.teams) {
      return 'Unknown Team';
    }
    
    // Find the team that's not the current user's team
    const partnerTeamId = trade.teams.find((team: any) => team.teamId !== userTeam.id)?.teamId;
    
    if (!partnerTeamId) {
      return 'Unknown Team';
    }
    
    // Look up the team name in the current league
    const partnerTeam = Object.values(currentLeague.teams).find((team: any) => team.id === partnerTeamId);
    
    return partnerTeam?.name || 'Unknown Team';
  }, [userTeam?.id, currentLeague?.teams]);
  
  const menuOptions: MenuOption[] = [
    {
      label: 'My Profile',
      icon: <PersonIcon />,
      action: 'profile'
    },
    ...(hasMultipleLeagues ? [{
      label: 'Switch League',
      icon: <SportsIcon />,
      action: 'leagues',
      divider: true
    }] : []),
    {
      label: 'Settings',
      icon: <SettingsIcon />,
      action: 'settings'
    },
    {
      label: 'Help & Support',
      icon: <HelpIcon />,
      action: 'help',
      divider: true
    },
    {
      label: 'Logout',
      icon: <LogoutIcon />,
      action: 'logout'
    }
  ];

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };


  const handleUserMenuClick = (action: string, event?: React.MouseEvent<HTMLElement>) => {
    switch(action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'leagues':
        if (event) {
          setAnchorElLeagues(event.currentTarget);
          return; // Don't close user menu yet
        }
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'help':
        // Navigate to help and support page
        navigate('/help');
        break;
      case 'logout':
        logOut(() => {
        	window.location.assign('/');
        });
        break;
      default:
        console.log('Unknown action:', action);
    }

    handleCloseUserMenu();
  };

  const handleOpenTradesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTrades(event.currentTarget);
  };

  const handleCloseTradesMenu = () => {
    setAnchorElTrades(null);
  };

  const handleCloseLeaguesMenu = () => {
    setAnchorElLeagues(null);
  };

  const handleLeagueSwitch = (targetLeagueId: string) => {
    navigate(`/league/${targetLeagueId}`);
    handleCloseLeaguesMenu();
    handleCloseUserMenu();
  };

  const handleLoginButtonPressed = () => {
    setLoginDialogOpen(true);
  }

  const handleCloseLoginDialog = () => {
    setLoginDialogOpen(false);
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // If the user is null then we want to show the login button
  if (user === null || Object.keys(user).length === 0) {
    return (
      <>
        <Button 
          variant="outlined" 
          sx={{ 
            my: 1, 
            mx: 1.5, 
            color: 'white',
            borderColor: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }} 
          onClick={handleLoginButtonPressed}
        >
          Login
        </Button>
        <LoginDialog 
          open={loginDialogOpen} 
          onClose={handleCloseLoginDialog} 
        />
      </>
    );
  }

  return (
    <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 1 }}> 
      {/* Trade Notifications - Only show when in league with pending trades */}
      {isInLeague && pendingTrades.length > 0 && (
        <Tooltip title={`${pendingTrades.length} Pending Trade${pendingTrades.length > 1 ? 's' : ''} - Click to view details`}>
          <IconButton
            onClick={handleOpenTradesMenu}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Badge badgeContent={pendingTrades.length} color="error">
              <SwapHorizIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Account Settings">
        <Box 
          onClick={handleOpenUserMenu} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <Typography 
            variant="body1" 
            color="inherit"
            sx={{ 
              display: { xs: 'none', sm: 'block' } // Hide username on mobile
            }}
          >
            {user.username || ''}
          </Typography>
          <Avatar 
            alt={user.username} 
            src={user.picture} 
            sx={{ width: 32, height: 32 }}
          />
        </Box>
      </Tooltip>
      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1.5,
          },
        }}
      > 
        {menuOptions.map((option, index) => (
          <React.Fragment key={option.action}>
            <MenuItem 
              onClick={(e) => handleUserMenuClick(option.action, e)}
              sx={{ py: 1.5, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {option.badge ? (
                  <Badge badgeContent={option.badge} color="error" variant="standard">
                    {option.icon}
                  </Badge>
                ) : (
                  option.icon
                )}
              </ListItemIcon>
              <ListItemText primary={option.label} />
              {option.action === 'leagues' && hasMultipleLeagues && <ExpandMoreIcon />}
            </MenuItem>
            {option.divider && <Divider />}
          </React.Fragment>
        ))}
      </Menu>

      {/* League Switcher Menu */}
      <Menu
        sx={{ mt: '45px' }}
        anchorEl={anchorElLeagues}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElLeagues)}
        onClose={handleCloseLeaguesMenu}
        PaperProps={{
          sx: {
            minWidth: 250,
            mt: 1.5,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Switch League
          </Typography>
        </Box>
        {userLeagues.map((league) => (
          <MenuItem 
            key={league.id}
            onClick={() => handleLeagueSwitch(league.id!)}
            sx={{ py: 1.5, px: 2 }}
            selected={league.id === leagueId}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {league.picture ? (
                <Avatar 
                  src={league.picture} 
                  sx={{ width: 32, height: 32 }}
                  alt={league.leagueName}
                />
              ) : (
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  <SportsIcon />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText 
              primary={league.leagueName || 'Unnamed League'}
              secondary={league.phase ? `${league.phase.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}` : 'Unknown Phase'}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Trade Notifications Menu */}
      <Menu
        sx={{ mt: '45px' }}
        anchorEl={anchorElTrades}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElTrades)}
        onClose={handleCloseTradesMenu}
        PaperProps={{
          sx: {
            minWidth: 300,
            mt: 1.5,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Pending Trades ({pendingTrades.length})
          </Typography>
        </Box>
        {pendingTrades.map((trade) => {
          const tradePartnerName = getTradePartnerName(trade);
          const playerCount = trade.players?.length || 0;
          const pickCount = trade.picks?.length || 0;
          
          return (
            <MenuItem 
              key={trade.id}
              onClick={() => {
                navigate(`/league/${leagueId}/team/${userTeam?.id}/trades`);
                handleCloseTradesMenu();
              }}
              sx={{ py: 2, px: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <SwapHorizIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={`Trade with ${tradePartnerName}`}
                secondary={`${playerCount} players, ${pickCount} picks involved`}
              />
            </MenuItem>
          );
        })}
        {pendingTrades.length === 0 && (
          <MenuItem disabled sx={{ py: 2, px: 2 }}>
            <ListItemText primary="No pending trades" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
export default ProfileButton;